import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, message, Spin, Space } from 'antd';
import { SettingOutlined, SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import { supabase } from '../lib/supabase';

const Config = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [configId, setConfigId] = useState(null);

  // Fetch existing config on component mount
  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setFetchLoading(true);
      const { data, error } = await supabase
        .from('config')
        .select('*')
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        throw error;
      }

      if (data) {
        setConfigId(data.id);
        form.setFieldsValue({
          url: data.url,
          username: data.username,
          password: data.password
        });
      }
    } catch (error) {
      console.error('Error fetching config:', error);
      message.error('Failed to fetch configuration');
    } finally {
      setFetchLoading(false);
    }
  };

  const handleSave = async (values) => {
    try {
      setLoading(true);
      
      const configData = {
        url: values.url,
        username: values.username,
        password: values.password,
        updated_at: new Date().toISOString()
      };

      let result;
      if (configId) {
        // Update existing config
        result = await supabase
          .from('config')
          .update(configData)
          .eq('id', configId)
          .select()
          .single();
      } else {
        // Create new config
        result = await supabase
          .from('config')
          .insert([{
            ...configData,
            created_at: new Date().toISOString()
          }])
          .select()
          .single();
      }

      if (result.error) {
        throw result.error;
      }

      if (result.data && !configId) {
        setConfigId(result.data.id);
      }

      message.success('Configuration saved successfully!');
    } catch (error) {
      console.error('Error saving config:', error);
      message.error('Failed to save configuration: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    fetchConfig();
  };

  if (fetchLoading) {
    return (
      <Card title="Configuration" style={{ textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading configuration...</div>
      </Card>
    );
  }

  return (
    <Card 
      title={
        <Space>
          <SettingOutlined />
          Configuration Settings
        </Space>
      }
      extra={
        <Button 
          icon={<ReloadOutlined />}
          onClick={fetchConfig}
          loading={fetchLoading}
          type="text"
          size="small"
        >
          Refresh
        </Button>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        style={{ maxWidth: 600 }}
      >
        <Form.Item
          label="URL"
          name="url"
          rules={[
            { required: true, message: 'Please enter the URL' },
            { type: 'url', message: 'Please enter a valid URL' }
          ]}
        >
          <Input 
            placeholder="https://example.com"
            size="large"
          />
        </Form.Item>

        <Form.Item
          label="Username"
          name="username"
          rules={[
            { required: true, message: 'Please enter the username' },
            { min: 2, message: 'Username must be at least 2 characters' }
          ]}
        >
          <Input 
            placeholder="Enter username"
            size="large"
          />
        </Form.Item>

        <Form.Item
          label="Password"
          name="password"
          rules={[
            { required: true, message: 'Please enter the password' },
            { min: 6, message: 'Password must be at least 6 characters' }
          ]}
        >
          <Input.Password 
            placeholder="Enter password"
            size="large"
          />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              icon={<SaveOutlined />}
              size="large"
            >
              Save Configuration
            </Button>
            <Button 
              onClick={handleReset}
              size="large"
            >
              Reset
            </Button>
          </Space>
        </Form.Item>
      </Form>

      {configId && (
        <div style={{ marginTop: 16, padding: 12, background: '#f6f8fa', borderRadius: 6 }}>
          <small style={{ color: '#666' }}>
            Configuration ID: {configId}
          </small>
        </div>
      )}
    </Card>
  );
};

export default Config;